import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Navbar from "./Navbar";
import Footer from "./Footer";

// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

const alumniData = [
  {
    name: "Sophia Tan",
    role: "Safety Compliance Manager",
    image:
      "https://images.unsplash.com/photo-1607746882042-944635dfe10e?auto=format&fit=crop&w=500&q=80",
    quote:
      "Greenmark transformed my understanding of workplace safety — today I lead compliance programs across Asia.",
  },
  {
    name: "Arjun Mehta",
    role: "Environmental Consultant",
    image:
      "https://images.unsplash.com/photo-1603415526960-f7e0328a50a9?auto=format&fit=crop&w=500&q=80",
    quote:
      "The practical training and expert mentors helped me launch my career in environmental management.",
  },
  {
    name: "Hannah Lee",
    role: "Sustainability Officer",
    image:
      "https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?auto=format&fit=crop&w=500&q=80",
    quote:
      "Being part of Greenmark's alumni network keeps me inspired and connected with industry leaders.",
  },
];

const AlumniPage = () => {
  const sectionRef = useRef(null);
  const heroRef = useRef(null);
  const alumniCardsRef = useRef(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate hero section
      gsap.fromTo(
        heroRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: heroRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Animate alumni cards section
      gsap.fromTo(
        alumniCardsRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: alumniCardsRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Animate CTA section
      gsap.fromTo(
        ctaRef.current,
        { opacity: 0, scale: 0.9 },
        {
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: ctaRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={sectionRef} className="bg-white text-gray-900 font-sans overflow-x-hidden scroll-smooth">
      <Navbar />
      {/* Hero Section with Parallax */}
      <section ref={heroRef} className="relative h-[60vh] flex items-center justify-center overflow-hidden pt-32">
        <div
          className="absolute inset-0 bg-fixed bg-center bg-cover"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=1600&q=80')",
          }}
        ></div>
        <div className="absolute inset-0 bg-[#0b9949]/70 backdrop-blur-sm"></div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl font-bold mb-4"
          >
            Greenmark Alumni Network
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg max-w-2xl mx-auto"
          >
            Join a growing community of sustainability leaders and safety
            professionals shaping a greener tomorrow.
          </motion.p>
        </div>
      </section>

      {/* Alumni Cards */}
      <section ref={alumniCardsRef} className="py-20 px-6 md:px-12 bg-[#f6fdf9]">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-[#0b9949] mb-12">
          Meet Our Alumni
        </h2>
        <div className="grid md:grid-cols-3 gap-10 max-w-6xl mx-auto">
          {alumniData.map((alum, i) => (
            <motion.div
              key={i}
              whileHover={{ scale: 1.05 }}
              className="bg-white rounded-2xl shadow-lg overflow-hidden text-center border border-gray-100"
            >
              <img
                src={alum.image}
                alt={alum.name}
                className="w-full h-64 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-[#0b9949] mb-1">
                  {alum.name}
                </h3>
                <p className="text-gray-600 text-sm mb-3">{alum.role}</p>
                <p className="text-gray-700 italic text-sm">
                  "{alum.quote}"
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Join Alumni CTA */}
      <section ref={ctaRef} className="relative py-20 bg-[#0b9949] text-white text-center overflow-hidden">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl font-bold mb-4">Become Part of the Legacy</h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto">
            Stay connected, share your journey, and collaborate with fellow
            Greenmark professionals making an impact globally.
          </p>
          <button className="bg-white text-[#0b9949] px-8 py-3 rounded-full font-medium hover:bg-[#f6fdf9] hover:scale-105 transition">
            Join Alumni Network
          </button>
        </motion.div>
      </section>
      <Footer />
    </div>
  );
};

export default AlumniPage;
